import { MessageGenerateAdapter, MessageTemplate, MessageTemplatePlaceholder } from '../message-generate.model';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { VindicationApiService } from '@/vindication/vindication.api.service';
import { MailBulkSendPayload, MailPreviewResponse } from '@/vindication/vindication.model';

@Injectable({ providedIn: 'root' })
export class MailGenerateAdapter implements MessageGenerateAdapter<MailPreviewResponse, MailBulkSendPayload[]> {
	private selectedTemplateId: number | null = null;
	private inOneMail = false;

	constructor(private readonly api: VindicationApiService) {}

	fetchTemplates(): Observable<MessageTemplate[]> {
		return this.api.getMailTemplateList(this.inOneMail);
	}

	setInOneMail(inOneMail: boolean): void {
		this.inOneMail = inOneMail;
	}

	fetchPlaceholders(): Observable<MessageTemplatePlaceholder[]> {
		return this.api.getMailTemplatePlaceholders();
	}

	createPreview({
		itemsIds,
		message,
		subject,
		template,
	}: {
		itemsIds: number[];
		message: string;
		subject: string;
		template?: number;
	}): Observable<MailPreviewResponse[]> {
		this.selectedTemplateId = template ?? null;
		return this.api.createMailPreview({ invoices: itemsIds, message, subject, template });
	}

	sendBulk(
		preview: MailPreviewResponse[],
		cc?: string[],
		attachmentsByItem?: Record<number, File[]>,
	): Observable<MailBulkSendPayload[]> {
		const mailsWithoutAttachments = preview.filter((item) => {
			const attachments = attachmentsByItem?.[item.invoice];
			return !attachments || attachments.length === 0;
		});

		const mailsWithAttachments = preview.filter((item) => {
			const attachments = attachmentsByItem?.[item.invoice];
			return !!attachments && attachments.length > 0;
		});

		const bulkPayload: MailBulkSendPayload[] = mailsWithoutAttachments.map((item) =>
			this.buildMailPayload(item, cc),
		);

		const bulkRequest$ = bulkPayload.length
			? this.api.createMailBulkSend(bulkPayload)
			: of<MailBulkSendPayload[]>([]);

		const attachmentRequests$ = mailsWithAttachments.length
			? forkJoin(
					mailsWithAttachments.map((item) => {
						const payload = this.buildMailPayload(item, cc);
						const attachments = attachmentsByItem?.[item.invoice] ?? [];

						return this.api.createMailWithAttachment(payload, attachments);
					}),
				)
			: of<MailBulkSendPayload[]>([]);

		return forkJoin([bulkRequest$, attachmentRequests$]).pipe(
			map(([bulkResult, attachmentsResult]) => {
				return [...bulkResult, ...attachmentsResult];
			}),
		);
	}

	private buildMailPayload(item: MailPreviewResponse, cc?: string[]): MailBulkSendPayload {
		const template = item.templateId ?? this.selectedTemplateId ?? 1;

		return {
			mails: item.mails,
			subject: item.subject,
			message: item.message,
			invoice: item.invoice,
			template,
			...(cc && cc.length > 0 && { cc }),
		};
	}
}
